import React, { useState, createElement } from 'react';
import { SearchIcon, XIcon, MailIcon, MailOpenIcon, ReplyIcon, ArchiveIcon, TrashIcon, AlertCircleIcon } from 'lucide-react';
import { useContact, ContactMessage } from './ContactContext';
import { ContactDetails } from './ContactDetails';
export function ContactList() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const {
    contactMessages,
    loading,
    updateContactMessage,
    deleteContactMessage
  } = useContact();
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const filteredMessages = contactMessages.filter(message => {
    const matchesSearch = searchQuery === '' || message.name.includes(searchQuery) || message.email.includes(searchQuery) || message.subject.includes(searchQuery) || message.message.includes(searchQuery);
    const matchesStatus = statusFilter === null || message.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const handleViewDetails = (message: ContactMessage) => {
    // If message is new, mark it as read
    if (message.status === 'new') {
      updateContactMessage(message.id, {
        status: 'read'
      });
    }
    setSelectedMessage(message);
    setShowDetailsModal(true);
  };
  const handleMarkAsRead = (message: ContactMessage, e: React.MouseEvent) => {
    e.stopPropagation();
    updateContactMessage(message.id, {
      status: 'read'
    });
  };
  const handleMarkAsReplied = (message: ContactMessage, e: React.MouseEvent) => {
    e.stopPropagation();
    updateContactMessage(message.id, {
      status: 'replied'
    });
  };
  const handleArchive = (message: ContactMessage, e: React.MouseEvent) => {
    e.stopPropagation();
    updateContactMessage(message.id, {
      status: 'archived'
    });
  };
  const handleDelete = (message: ContactMessage, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedMessage(message);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (selectedMessage) {
      deleteContactMessage(selectedMessage.id);
      setShowDeleteConfirm(false);
      setSelectedMessage(null);
      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-24 right-1/2 transform translate-x-1/2 bg-green-100 text-green-800 px-4 py-2 rounded-full shadow-lg z-50 animate-fadeIn';
      notification.textContent = 'تم حذف الرسالة بنجاح';
      document.body.appendChild(notification);
      setTimeout(() => {
        notification.classList.add('opacity-0');
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, 2000);
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-705">
            <AlertCircleIcon className="h-3 w-3 ml-1" data-id="element-706" />
            جديدة
          </span>;
      case 'read':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-707">
            <MailOpenIcon className="h-3 w-3 ml-1" data-id="element-708" />
            مقروءة
          </span>;
      case 'replied':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-709">
            <ReplyIcon className="h-3 w-3 ml-1" data-id="element-710" />
            تم الرد
          </span>;
      case 'archived':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800" data-id="element-711">
            <ArchiveIcon className="h-3 w-3 ml-1" data-id="element-712" />
            مؤرشفة
          </span>;
      default:
        return null;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  if (loading) {
    return <div className="bg-white rounded-lg shadow-md p-6" data-id="element-713">
        <div className="flex justify-center items-center h-64" data-id="element-714">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-sky-600 border-t-transparent" data-id="element-715"></div>
        </div>
      </div>;
  }
  return <div className="bg-white rounded-lg shadow-md p-6" data-id="element-716">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4" data-id="element-717">
        <h2 className="text-xl font-bold text-sky-900" data-id="element-718">رسائل التواصل</h2>
        <div className="flex gap-4 w-full md:w-auto" data-id="element-719">
          <div className="relative flex-1 md:w-64" data-id="element-720">
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="بحث في الرسائل..." className="w-full px-4 py-2 pr-10 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" data-id="element-721" />
            <SearchIcon className="absolute top-2.5 right-3 h-5 w-5 text-slate-400" data-id="element-722" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute top-2.5 left-3 h-5 w-5 text-slate-400 hover:text-slate-600" data-id="element-723">
                <XIcon className="h-5 w-5" data-id="element-724" />
              </button>}
          </div>
        </div>
      </div>
      <div className="mb-6 flex flex-wrap gap-2" data-id="element-725">
        <div data-id="element-726">
          <span className="block text-sm font-medium text-slate-700 mb-1" data-id="element-727">
            تصفية حسب الحالة:
          </span>
          <div className="flex flex-wrap gap-2" data-id="element-728">
            <button onClick={() => setStatusFilter(null)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === null ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-700 hover:bg-sky-50'}`} data-id="element-729">
              الكل
            </button>
            <button onClick={() => setStatusFilter('new')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === 'new' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-blue-50'}`} data-id="element-730">
              جديدة
            </button>
            <button onClick={() => setStatusFilter('read')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === 'read' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700 hover:bg-yellow-50'}`} data-id="element-731">
              مقروءة
            </button>
            <button onClick={() => setStatusFilter('replied')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === 'replied' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700 hover:bg-green-50'}`} data-id="element-732">
              تم الرد
            </button>
            <button onClick={() => setStatusFilter('archived')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === 'archived' ? 'bg-slate-200 text-slate-700' : 'bg-gray-100 text-gray-700 hover:bg-slate-50'}`} data-id="element-733">
              مؤرشفة
            </button>
          </div>
        </div>
      </div>
      {filteredMessages.length === 0 ? <div className="text-center py-12 bg-slate-50 rounded-lg" data-id="element-734">
          <p className="text-slate-500 mb-2" data-id="element-735">لا توجد رسائل مطابقة لبحثك</p>
          {(searchQuery || statusFilter) && <button onClick={() => {
        setSearchQuery('');
        setStatusFilter(null);
      }} className="text-sky-600 hover:text-sky-800 font-medium" data-id="element-736">
              عرض جميع الرسائل
            </button>}
        </div> : <div className="overflow-x-auto" data-id="element-737">
          <table className="min-w-full divide-y divide-slate-200" data-id="element-738">
            <thead className="bg-slate-50" data-id="element-739">
              <tr data-id="element-740">
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-741">
                  المرسل
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-742">
                  الموضوع
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-743">
                  التاريخ
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-744">
                  الحالة
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-745">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200" data-id="element-746">
              {filteredMessages.map(message => <tr key={message.id} className={`hover:bg-slate-50 cursor-pointer ${message.status === 'new' ? 'bg-blue-50' : ''}`} onClick={() => handleViewDetails(message)} data-id="element-747">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-748">
                    <div className="flex flex-col" data-id="element-749">
                      <div className="text-sm font-medium text-slate-900" data-id="element-750">
                        {message.name}
                      </div>
                      <div className="text-sm text-slate-500" data-id="element-751">
                        {message.email}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-752">
                        {message.phone}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-753">
                    <div className="text-sm text-slate-900 line-clamp-2" data-id="element-754">
                      {message.subject}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-755">
                    <div className="text-sm text-slate-500" data-id="element-756">
                      {formatDate(message.createdAt)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-757">
                    {getStatusBadge(message.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium" data-id="element-758">
                    <div className="flex justify-center space-x-2 rtl:space-x-reverse" data-id="element-759">
                      {message.status === 'new' && <button onClick={e => handleMarkAsRead(message, e)} className="text-blue-600 hover:text-blue-900 p-1" title="تحديد كمقروءة" data-id="element-760">
                          <MailOpenIcon className="h-5 w-5" data-id="element-761" />
                        </button>}
                      {(message.status === 'new' || message.status === 'read') && <button onClick={e => handleMarkAsReplied(message, e)} className="text-green-600 hover:text-green-900 p-1" title="تحديد كتم الرد" data-id="element-762">
                          <ReplyIcon className="h-5 w-5" data-id="element-763" />
                        </button>}
                      {message.status !== 'archived' && <button onClick={e => handleArchive(message, e)} className="text-slate-600 hover:text-slate-900 p-1" title="أرشفة" data-id="element-764">
                          <ArchiveIcon className="h-5 w-5" data-id="element-765" />
                        </button>}
                      <button onClick={e => handleDelete(message, e)} className="text-red-600 hover:text-red-900 p-1" title="حذف" data-id="element-766">
                        <TrashIcon className="h-5 w-5" data-id="element-767" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}
      {showDetailsModal && selectedMessage && <ContactDetails message={selectedMessage} onClose={() => {
      setShowDetailsModal(false);
      setSelectedMessage(null);
    }} onStatusChange={status => {
      updateContactMessage(selectedMessage.id, {
        status
      });
    }} data-id="element-768" />}
      {showDeleteConfirm && selectedMessage && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" data-id="element-769">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden" data-id="element-770">
            <div className="p-4 bg-red-50 border-b border-red-100 flex justify-between items-center" data-id="element-771">
              <h3 className="font-bold text-red-900" data-id="element-772">تأكيد الحذف</h3>
              <button onClick={() => {
            setShowDeleteConfirm(false);
            setSelectedMessage(null);
          }} className="p-1.5 rounded-full hover:bg-red-100 text-slate-500 hover:text-slate-700 transition-colors" data-id="element-773">
                <XIcon className="h-5 w-5" data-id="element-774" />
              </button>
            </div>
            <div className="p-6" data-id="element-775">
              <p className="text-slate-700 mb-4" data-id="element-776">
                هل أنت متأكد من رغبتك في حذف هذه الرسالة؟ لا يمكن التراجع عن هذا
                الإجراء.
              </p>
              <div className="flex justify-between gap-4" data-id="element-777">
                <button onClick={() => {
              setShowDeleteConfirm(false);
              setSelectedMessage(null);
            }} className="px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 transition-colors duration-200 flex-1" data-id="element-778">
                  إلغاء
                </button>
                <button onClick={confirmDelete} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200 flex-1" data-id="element-779">
                  نعم، حذف
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}